import { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Tooltip } from "antd";
import classNames from "classnames";
import { DateTime } from "luxon";
import { v4 as uuidv4 } from "uuid";
import { LoadingStatus, GridRowHeightEnum } from "../../../@types/enumsGlobal";
import { DownIcon, RightIcon } from "../../../libs/corporate/icons/outlined/directions/ChevronCollection";
import { FolderIcon, FolderMinusIcon, FolderPlusIcon } from "../../../libs/corporate/icons/outlined/other/FileAndFolderCollection";
import * as ActionIcons from "../../../libs/corporate/icons/outlined/editor/ActionCollection";
import {} from "../../../Services/Extensions/Boolean";
import {} from "../../../Services/Extensions/DateTime";
import { copyToClipboard } from "../../../system/functions/copyToClipboard";
import { FormattedValueView, getFormattedCellValue } from "../components/FormattedValueView";
import { TableHeader } from "../components/TableHeader";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { useUserContext } from "../../../system/providers/userContextProvider";
import { Spinner } from "../../spiner/Spinner";
import { DocumentOperationSpinner } from "../../DocumentOperationSpinner/DocumentOperationSpinner";
import { ContextMenu } from "../../contextMenu/contextMenu";
import { IColumnToSave, IGridCellWithWidth, IPage } from "../default/defaultGrid";
import defaultStyles from "../default/defaultGrid.module.scss";
import GridUISettings from "../default/gridUiSettings";
import styles from "./treeGrid.module.scss";
import { reverseConversionWithType } from "../../../system/functions/conversion";

function compareByOrder(a: { order?: number }, b: { order?: number }) {
    if (a.order === undefined || b.order === undefined) return 0;
    if (a.order < b.order) return -1;
    if (a.order > b.order) return 1;
    return 0;
}

interface ITreeGridProps {
    gridId: string;
    plugin: IPluginSettings;
    data: any[];
    filter: IGridFilter;
    totalCount: number;
    idParent: string; // Наименование поля с уникальным ID элемента (по нему ищем детей)
    idChildren: string; // Наименование поля со ссылкой на родителя (равно idParent родительского элемента)
    selectedItem?: IGridRow;
    loadingStatus?: LoadingStatus;
    setLoadingStatus?: (value: LoadingStatus) => void;
    showQuantity?: boolean;
    getStyleRow?: (row: IGridRow) => string | null;
    onSelect?: (row: IGridRow | undefined) => void;
    onDoubleClick?: (row: IGridRow) => void;
    useUiService?: boolean;
    changeData?: (data: any[]) => any[]; // Функция для подготовки данных перед построением иерархии
}

interface ITreeVisibleNode {
    rawData: any;
    row: IGridRow;
    depth: number;
    hasChildren: boolean;
}

export const TreeGrid: FC<ITreeGridProps> = (props) => {
    const appCtx = useAppContext();
    const userCtx = useUserContext();
    const tableRef = useRef<HTMLDivElement>(null);
    const gridRef = useRef<HTMLTableSectionElement>(null);

    const [useUiService] = useState<boolean>(props.useUiService ?? true);
    const defaultPlugin: IPluginSettings = JSON.parse(JSON.stringify(props.plugin)) as IPluginSettings;
    const [pluginSettings, setPluginSettings] = useState<IPluginSettings>(defaultPlugin);
    const [gridColumnsToSave, setGridColumnsToSave] = useState<IColumnToSave>({ columns: [], save: false });
    const assignIdRows = (items: any[]) =>
        items.map((x) => ({ ...x, idRow: uuidv4() }));

    const [data, setData] = useState<any[]>(() => assignIdRows(props.data));
    const [selectedItem, setSelectedItem] = useState<IGridRow | undefined>(undefined);
    const [loadingRow, setLoadingRow] = useState<LoadingStatus>(LoadingStatus.InProcess);
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

    // Нужен только для TableHeader (не используется для пагинации)
    const [, setPageNumber] = useState<IPage>({
        pageNumber: props.filter.pageNumber,
        prevPageNumber: props.filter.pageNumber,
    });

    const savedScrollLeft = useRef<number | null>(null);
    const scrollLockCleanup = useRef<(() => void) | null>(null);

    const [gridHeaderContextMenuVisible, setGridHeaderContextMenuVisible] = useState(false);
    const [showGridHeaderSettings, setShowGridHeaderSettings] = useState(false);
    const [headerCtxPosX, setHeaderCtxPosX] = useState(0);
    const [headerCtxPosY, setHeaderCtxPosY] = useState(0);

    useEffect(() => {
        setData(assignIdRows(props.data));
    }, [props.data]);

    useEffect(() => {
        setSelectedItem(props.selectedItem);
    }, [props.selectedItem]);

    useEffect(() => {
        pluginSettingsLoad();
        gridRef?.current?.focus();
    }, []);

    useEffect(() => {
        if (useUiService && gridColumnsToSave.save) {
            appCtx.uiApiService.uiPluginSettingsRequest.save(
                {
                    idUser: userCtx.idGlobal,
                    name: pluginSettings.name,
                    mnemocode: pluginSettings.mnemocode,
                    gridColumns: gridColumnsToSave.columns,
                    detailsPlugins: [] as string[],
                    rowHeight: pluginSettings.rowHeight ?? GridRowHeightEnum.normal,
                },
                () => {
                    setPluginSettings({ ...pluginSettings, columns: gridColumnsToSave.columns });
                    setGridColumnsToSave({ ...gridColumnsToSave, save: false });
                }
            );
        }
    }, [gridColumnsToSave]);

    useLayoutEffect(() => {
        if (savedScrollLeft.current !== null && tableRef.current) {
            tableRef.current.scrollLeft = savedScrollLeft.current;
            if (!gridColumnsToSave.save) {
                scrollLockCleanup.current?.();
                scrollLockCleanup.current = null;
                savedScrollLeft.current = null;
            }
        }
    }, [pluginSettings.columns, gridColumnsToSave.save]);

    const pluginSettingsLoad = () => {
        if (useUiService) {
            setLoadingRow(LoadingStatus.InProcess);
            appCtx.uiApiService.uiPluginSettingsRequest.load(pluginSettings.mnemocode, userCtx.idGlobal, (e) => {
                if (e.respType === "isCompleted") {
                    const columns: IGridColumn[] = [];
                    e.data.gridColumns.forEach((item) => columns.push(item));
                    const plugin: IPluginSettings = {
                        name: e.data.name,
                        mnemocode: e.data.mnemocode,
                        columns: [...columns],
                        permission: pluginSettings.permission,
                        rowHeight: e.data.rowHeight,
                    };
                    setPluginSettings({ ...plugin });
                    setLoadingRow(LoadingStatus.Completed);
                } else {
                    appCtx.uiApiService.uiPluginSettingsRequest.save(
                        {
                            idUser: userCtx.idGlobal,
                            name: pluginSettings.name,
                            mnemocode: pluginSettings.mnemocode,
                            gridColumns: pluginSettings.columns,
                            detailsPlugins: [] as string[],
                            rowHeight: pluginSettings.rowHeight ?? GridRowHeightEnum.normal,
                        },
                        () => setLoadingRow(LoadingStatus.Completed)
                    );
                }
            });
        } else {
            setLoadingRow(LoadingStatus.Completed);
        }
    };

    function getContextId(baseId: string) {
        return `ctxMenu_header_${baseId}`;
    }

    useEffect(() => {
        const handler = () => setGridHeaderContextMenuVisible(false);
        if (gridHeaderContextMenuVisible === true) {
            let ctxMenu = document.getElementById(getContextId(props.gridId)) as HTMLElement;
            if (ctxMenu) {
                ctxMenu.onmouseleave = handler;
                return ctxMenu.removeEventListener("mouseleave", handler);
            }
        }
    }, [gridHeaderContextMenuVisible]);

    const saveColumnWidths = () => {
        const grid = document.getElementById(props.gridId) as HTMLElement;
        if (!grid) return;
        const columnCells = grid.getElementsByClassName(defaultStyles.gridColumnCell);
        const newState: IGridColumn[] = [...pluginSettings.columns];
        let changed = false;
        for (let i = 0; i < columnCells.length; i++) {
            const pName = columnCells[i].getAttribute("data-property-name");
            const column = newState.find((x) => x.propertyName === pName);
            const rect = columnCells[i].getBoundingClientRect().width;
            if (column && column.width !== rect) {
                changed = true;
                column.width = parseInt(String(rect));
            }
        }
        if (changed) {
            setGridColumnsToSave({ columns: newState, save: true });
        }
    };

    const buildTreeMaps = (source: any[]) => {
        const childrenMap = new Map<string, any[]>();
        const roots: any[] = [];

        for (const item of source) {
            const parentRef = item[props.idChildren];
            if (!parentRef) {
                roots.push(item);
            } else {
                if (!childrenMap.has(parentRef)) {
                    childrenMap.set(parentRef, []);
                }
                childrenMap.get(parentRef)!.push(item);
            }
        }

        return { roots, childrenMap };
    };

    const convertToRow = (rawData: any): IGridRow => {
        const cells: IGridCell[] = [];

        Object.entries(rawData).forEach(([propertyName, value]) => {
            try {
                const column = pluginSettings.columns.find((c) => c.propertyName === propertyName);
                let val: any = value;

                const translation =
                    column?.translation ??
                    props.plugin.columns.find((x) => x.propertyName === propertyName)?.translation;
                const conversion =
                    column?.conversion ??
                    props.plugin.columns.find((x) => x.propertyName === propertyName)?.conversion;

                if (translation) {
                    val = Object.entries(rawData).find((x) => x[0] === translation)?.[1];
                }
                if (conversion) {
                    val = reverseConversionWithType(conversion, val, true);
                }

                const nullable =
                    column?.nullable ??
                    props.plugin.columns.find((x) => x.propertyName === propertyName)?.nullable;

                if (val && val !== null && val !== "" && column) {
                    switch (column.propertyType) {
                        case "boolean":
                            val = (val as boolean).toYesNoString();
                            break;
                        case "date":
                            val = typeof val !== "string" ? (val as DateTime).toF3DateFormat(userCtx) : null;
                            break;
                        case "datetime":
                            val = typeof val !== "string" ? (val as DateTime).toF3DateTimeFormat(userCtx) : null;
                            break;
                    }
                }

                cells.push({
                    value: val,
                    propertyName,
                    order: column?.order as number,
                    propertyType: column?.propertyType as string,
                    visibility: pluginSettings.columns.find((x) => x.propertyName === propertyName)
                        ?.visibility as boolean,
                    nullable,
                });
            } catch {
                // skip unknown fields
            }
        });

        return {
            idGlobal: rawData["idGlobal"] as string,
            idRow: rawData["idRow"] as string,
            displayName: rawData["displayName"] || rawData["name"],
            cells: cells
                .filter((c) => c.order !== undefined && c.order !== null)
                .sort(compareByOrder),
            isDeleted: rawData["deleted"] as boolean,
            isLocked: rawData["isLocked"] as boolean,
            isSelected: rawData["idRow"] === selectedItem?.idRow,
        };
    };

    const getVisibleNodes = (
        items: any[],
        childrenMap: Map<string, any[]>,
        depth = 0
    ): ITreeVisibleNode[] => {
        const result: ITreeVisibleNode[] = [];
        for (const rawData of items) {
            const ownId = rawData[props.idParent];
            const children = childrenMap.get(ownId) ?? [];
            const hasChildren = children.length > 0;
            const row = convertToRow(rawData);
            result.push({ rawData, row, depth, hasChildren });
            if (hasChildren && expandedIds.has(rawData.idRow)) {
                result.push(...getVisibleNodes(children, childrenMap, depth + 1));
            }
        }
        return result;
    };

    const toggleExpand = (id: string) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    // ── Render ──

    if (loadingRow === LoadingStatus.InProcess) return <Spinner />;

    if (props.loadingStatus === LoadingStatus.InProcess) return <DocumentOperationSpinner xl />;

    if (props.loadingStatus === LoadingStatus.NoAccess)
        return <div className={defaultStyles.noDataInfo}><span>Нет доступа</span></div>;

    if (props.loadingStatus === LoadingStatus.NoData)
        return <div className={defaultStyles.noDataInfo}><span>Нет данных</span></div>;

    const treeData = props.changeData ? props.changeData(data) : data;
    const { roots, childrenMap } = buildTreeMaps(treeData);
    const visibleNodes = getVisibleNodes(roots, childrenMap);

    // Ширина колонки дерева: отступ (25px × уровень) + иконки кнопки (~38px)
    const maxDepth = visibleNodes.reduce((max, n) => Math.max(max, n.depth), 0);
    const treeColWidth = Math.max(50, maxDepth * 25 + 38);

    const displayColumns =
        gridColumnsToSave.save && gridColumnsToSave.columns.length > 0
            ? gridColumnsToSave.columns
            : pluginSettings.columns;

    return (
        <div className={defaultStyles.gridWrapper}>
            {props.showQuantity && (
                <div className={defaultStyles.quantityPositions}>
                    Общее кол-во: <b>{props.totalCount}</b>
                </div>
            )}

            <div className={defaultStyles.gridWrapper__tables} ref={tableRef}>
                <table id={props.gridId} className={defaultStyles.gridTable}>

                    {/* ── Заголовок ── */}
                    <thead
                        className={defaultStyles.gridColumnsHeader}
                        style={{ height: pluginSettings.rowHeight }}
                        onContextMenu={(e) => {
                            if (process.env.REACT_APP_SHOW_GRID_CONTEXT === "true") {
                                e.preventDefault();
                                setHeaderCtxPosX(e.clientX);
                                setHeaderCtxPosY(e.clientY);
                                setGridHeaderContextMenuVisible(true);
                            }
                        }}
                    >
                        <tr
                            className={defaultStyles.gridColumn}
                            onMouseUp={saveColumnWidths}
                        >
                            {/* Фиксированная колонка для иконки дерева */}
                            <td draggable={false} className={styles.treeColumn} style={{ width: treeColWidth, minWidth: treeColWidth }} />

                            {displayColumns
                                .filter((c) => c.visibility)
                                .sort(compareByOrder)
                                .map((item, index) => (
                                    <TableHeader
                                        key={`th-${index}`}
                                        index={index}
                                        draggable={true}
                                        item={item}
                                        filtered={false}
                                        pluginSettings={pluginSettings}
                                        setGridColumnsToSave={setGridColumnsToSave}
                                        sorting={false}
                                        onSort={() => {}}
                                        searching={false}
                                        onSearch={() => {}}
                                        renderSortIcon={() => <></>}
                                        onFilterDelete={() => {}}
                                        filter={props.filter}
                                        setPageNumber={setPageNumber}
                                        setLoadingStatus={props.setLoadingStatus}
                                        onPageNumberChange={() => {}}
                                        onResizeStart={() => {
                                            const el = tableRef.current;
                                            if (!el) return;
                                            const lockedScrollLeft = el.scrollLeft;
                                            savedScrollLeft.current = lockedScrollLeft;
                                            let rafId: number | null = null;
                                            const lock = () => {
                                                if (el.scrollLeft !== lockedScrollLeft) {
                                                    el.scrollLeft = lockedScrollLeft;
                                                }
                                                rafId = requestAnimationFrame(lock);
                                            };
                                            rafId = requestAnimationFrame(lock);
                                            scrollLockCleanup.current = () => {
                                                if (rafId !== null) cancelAnimationFrame(rafId);
                                            };
                                        }}
                                        onReorderEnd={() => {
                                            const el = tableRef.current;
                                            if (!el) return;
                                            const lockedScrollLeft = el.scrollLeft;
                                            savedScrollLeft.current = lockedScrollLeft;
                                            let rafId: number | null = null;
                                            const lock = () => {
                                                if (el.scrollLeft !== lockedScrollLeft) {
                                                    el.scrollLeft = lockedScrollLeft;
                                                }
                                                rafId = requestAnimationFrame(lock);
                                            };
                                            rafId = requestAnimationFrame(lock);
                                            scrollLockCleanup.current = () => {
                                                if (rafId !== null) cancelAnimationFrame(rafId);
                                            };
                                        }}
                                        cls={{
                                            tD: defaultStyles.gridColumnCell,
                                            tooltip: defaultStyles.gridColumnHeaderName,
                                            sortingDiv: defaultStyles.gridColumnCellButtons,
                                            sortingDivButton: defaultStyles.gridSortButton,
                                            searchDropDivButton: defaultStyles.searchDropDivButton,
                                            filteredDiv: defaultStyles.gridFilterButton,
                                        }}
                                    />
                                ))}
                        </tr>
                    </thead>

                    {/* ── Строки ── */}
                    <tbody className={defaultStyles.gridRowsGroup} ref={gridRef}>
                        {visibleNodes.map(({ rawData, row, depth, hasChildren }, index) => {
                            const isExpanded = expandedIds.has(rawData.idRow);

                            let orderedCells: IGridCellWithWidth[] = row.cells.map((cell) => ({
                                ...cell,
                                order: displayColumns.find((x) => x.propertyName === cell.propertyName)
                                    ?.order as number,
                                width: displayColumns.find((x) => x.propertyName === cell.propertyName)
                                    ?.width as number,
                            }));
                            orderedCells = orderedCells.sort(compareByOrder);

                            return (
                                <tr
                                    key={`tree-row-${index}`}
                                    tabIndex={-1}
                                    data-item-id={row.idGlobal}
                                    className={classNames(
                                        defaultStyles.gridRow,
                                        props.getStyleRow?.(row),
                                        row.isSelected ? defaultStyles.gridRowSelected : null,
                                        row.isDeleted ? defaultStyles.gridRowDeleted : null,
                                        row.isLocked ? defaultStyles.gridRowLocked : null
                                    )}
                                    onClick={() => {
                                        if (row.isSelected) {
                                            setSelectedItem(undefined);
                                            props.onSelect?.(undefined);
                                        } else {
                                            setSelectedItem(row);
                                            props.onSelect?.(row);
                                        }
                                    }}
                                    onDoubleClick={() => props.onDoubleClick?.(row)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Escape" && selectedItem) {
                                            setSelectedItem(undefined);
                                            props.onSelect?.(undefined);
                                        }
                                    }}
                                >
                                    {/* Колонка дерева: отступ + иконка expand/collapse */}
                                    <td
                                        className={classNames(styles.treeColumn, styles.treeColumnData)}
                                        style={{ width: treeColWidth, minWidth: treeColWidth, height: pluginSettings.rowHeight }}
                                    >
                                        <div
                                            className={styles.treeIndent}
                                            style={{ paddingLeft: depth * 16 }}
                                        >
                                            <button
                                                className={styles.treeExpandButton}
                                                onClick={() => {
                                                    if (hasChildren) toggleExpand(rawData.idRow);
                                                }}
                                            >
                                                <div className={styles.treeChevron}>
                                                    {hasChildren && (
                                                        isExpanded
                                                            ? <DownIcon className={styles.treeSvg} />
                                                            : <RightIcon className={styles.treeSvg} />
                                                    )}
                                                </div>
                                                {hasChildren
                                                    ? isExpanded
                                                        ? <FolderMinusIcon className={styles.treeSvg} />
                                                        : <FolderPlusIcon className={styles.treeSvg} />
                                                    : <FolderIcon className={styles.treeSvg} />
                                                }
                                            </button>
                                        </div>
                                    </td>

                                    {/* Колонки данных */}
                                    {orderedCells.map(
                                        (cell, cellIndex) =>
                                            cell.visibility && (
                                                <TreeGridTd
                                                    key={cellIndex}
                                                    item={cell}
                                                    rowHeight={pluginSettings.rowHeight}
                                                />
                                            )
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {gridHeaderContextMenuVisible && (
                <ContextMenu
                    id={getContextId(props.gridId)}
                    xPos={headerCtxPosX}
                    yPos={headerCtxPosY}
                    items={[
                        {
                            name: "Настройки таблицы",
                            onClick: () => setShowGridHeaderSettings(true),
                        },
                    ]}
                />
            )}
            {showGridHeaderSettings && (
                <GridUISettings
                    plugin={pluginSettings}
                    defaultPlugin={defaultPlugin}
                    save={(e) => {
                        if (e.columns) {
                            setGridColumnsToSave({ columns: e.columns, save: true });
                        }
                        setPluginSettings({ ...pluginSettings, rowHeight: e.rowHeight });
                        setGridHeaderContextMenuVisible(false);
                        setShowGridHeaderSettings(false);
                    }}
                    cancel={() => {
                        setGridHeaderContextMenuVisible(false);
                        setShowGridHeaderSettings(false);
                    }}
                />
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
//  TreeGridTd — ячейка данных
// ─────────────────────────────────────────────────────────────────────────────

interface ITreeGridTdProps {
    item: IGridCellWithWidth;
    rowHeight?: number;
}

const TreeGridTd: FC<ITreeGridTdProps> = ({ item, rowHeight }) => {
    const [spanOverflows, setSpanOverflows] = useState<boolean>(false);
    const [formatTooltip, setFormatTooltip] = useState<string>("");
    const [copyVisible, setCopyVisible] = useState<boolean>(false);
    const [copyOpacity, setCopyOpacity] = useState<number>(0.4);
    const tdRef = useRef<HTMLTableCellElement>(null);

    const checkOverflow = () => {
        const el = tdRef.current;
        if (!el) return;
        const css = getComputedStyle(el);
        const innerWidth =
            el.offsetWidth - parseFloat(css.paddingLeft) - parseFloat(css.paddingRight) - 5;
        const span = el.querySelector("span");
        setSpanOverflows((span?.scrollWidth ?? 0) > innerWidth);
    };

    return (
        <td
            ref={tdRef}
            className={defaultStyles.gridRowCell}
            style={{ width: item.width, minWidth: item.width, height: rowHeight, cursor: "default" }}
            data-order-number={item.order}
            data-property-name={item.propertyName}
            onMouseEnter={() => {
                checkOverflow();
                setCopyVisible(true);
            }}
            onMouseLeave={() => setCopyVisible(false)}
        >
            <Tooltip
                className={defaultStyles.gridRowCellText}
                title={spanOverflows ? formatTooltip : undefined}
                placement="top"
                autoAdjustOverflow={false}
                overlayStyle={{ maxWidth: 300 }}
                getPopupContainer={() => document.body}
            >
                <span style={{ width: "100%" }}>
                    <FormattedValueView newValue={item} setFormatTooltip={setFormatTooltip} />
                </span>
            </Tooltip>
            <span
                style={{
                    opacity: copyVisible ? copyOpacity : 0,
                    transition: "opacity 0.3s ease-in",
                    marginRight: -10,
                    paddingLeft: 1,
                    width: 16,
                    height: 16,
                    cursor: "pointer",
                    backgroundColor: "white",
                    borderRadius: 3,
                }}
                onClick={async () => {
                    await copyToClipboard(getFormattedCellValue(item));
                }}
            >
                <ActionIcons.CopyMiniIcon
                    onMouseEnter={() => setCopyOpacity(0.85)}
                    onMouseLeave={() => setCopyOpacity(0.4)}
                />
            </span>
        </td>
    );
};
